<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import integrationAPI from 'dashboard/api/integrations';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CrmContactSelector from './CrmContactSelector.vue';
import CrmProductSelector from './CrmProductSelector.vue';

/**
 * @typedef {Object} ManualCrmContact
 * @property {number|string} id
 * @property {string|null} [name]
 * @property {string|null} [email]
 * @property {string|null} [phone_number]
 * @property {string|null} [phoneNumber]
 */

const props = defineProps({
  preselectedContact: {
    type: Object,
    default: null,
  },
  requireContactPicker: {
    type: Boolean,
    default: false,
  },
  initialEventType: {
    type: String,
    default: 'custom_note',
  },
});

const emit = defineEmits(['created']);

const { t } = useI18n();

/**
 * Builds the default manual event form state.
 *
 * @returns {Record<string, string>}
 */
function defaultForm() {
  return {
    summary: '',
    note: '',
    store_name: '',
    visit_reason: '',
    sales_associate_name: '',
    product_title: 'Product placeholder',
    offer_title: '',
    offer_code: '',
    expires_at: '',
    value: '',
    currency: 'EUR',
    follow_up_at: '',
    channel: '',
  };
}

const dialogRef = ref(null);
const isSubmitting = ref(false);
const errorMessage = ref('');
/** @type {import('vue').Ref<ManualCrmContact|null>} */
const selectedContact = ref(props.preselectedContact);
const contactSelectorResetKey = ref(0);
const selectedProduct = ref(null);
const selectedVariantId = ref('');
const productSelectorResetKey = ref(0);
const eventType = ref(props.initialEventType);
const form = ref(defaultForm());

const eventTypeOptions = computed(() => [
  {
    value: 'custom_note',
    label: t('CRM_EVENT_CREATE.EVENT_TYPES.CUSTOM_NOTE'),
  },
  {
    value: 'store_visit',
    label: t('CRM_EVENT_CREATE.EVENT_TYPES.STORE_VISIT'),
  },
  {
    value: 'product_recommended',
    label: t('CRM_EVENT_CREATE.EVENT_TYPES.PRODUCT_RECOMMENDED'),
  },
  {
    value: 'wishlist_item_added',
    label: t('CRM_EVENT_CREATE.EVENT_TYPES.WISHLIST_ITEM_ADDED'),
  },
  {
    value: 'offer_shared',
    label: t('CRM_EVENT_CREATE.EVENT_TYPES.OFFER_SHARED'),
  },
  {
    value: 'follow_up_promised',
    label: t('CRM_EVENT_CREATE.EVENT_TYPES.FOLLOW_UP_PROMISED'),
  },
]);

const showContactPicker = computed(
  () => props.requireContactPicker && !props.preselectedContact
);
const selectedContactSubtitle = computed(() =>
  [
    selectedContact.value?.email,
    selectedContact.value?.phoneNumber || selectedContact.value?.phone_number,
  ]
    .filter(Boolean)
    .join(' / ')
);
const needsProductPlaceholder = computed(() =>
  ['product_recommended', 'wishlist_item_added'].includes(eventType.value)
);
const selectedProductVariants = computed(
  () => selectedProduct.value?.variants || []
);
const selectedVariant = computed(() =>
  selectedProductVariants.value.find(
    variant => String(variant.id) === String(selectedVariantId.value)
  )
);
const canSubmit = computed(() => {
  if (!selectedContact.value?.id) return false;
  if (needsProductPlaceholder.value) return true;
  if (eventType.value === 'offer_shared') {
    return Boolean(form.value.offer_title);
  }
  if (eventType.value === 'follow_up_promised') {
    return Boolean(form.value.summary && form.value.follow_up_at);
  }
  return Boolean(form.value.summary);
});

/**
 * Resets transient state when the dialog is opened or event type changes.
 *
 * @returns {void}
 */
function resetForm() {
  form.value = defaultForm();
  errorMessage.value = '';
  selectedProduct.value = null;
  selectedVariantId.value = '';
  contactSelectorResetKey.value += 1;
  productSelectorResetKey.value += 1;
}

/**
 * Opens the underlying modal.
 *
 * @returns {void}
 */
function open() {
  resetForm();
  selectedContact.value = props.preselectedContact;
  eventType.value = props.initialEventType;
  dialogRef.value?.open();
}

/**
 * Closes the underlying modal.
 *
 * @returns {void}
 */
function close() {
  dialogRef.value?.close();
}

/**
 * Converts contact data from either snake_case or camelCase sources.
 *
 * @param {ManualCrmContact} contact Contact payload.
 * @returns {string}
 */
function contactDisplayName(contact) {
  return (
    contact?.name ||
    contact?.email ||
    contact?.phoneNumber ||
    contact?.phone_number ||
    t('CRM_EVENTS_FEED.UNKNOWN_CONTACT')
  );
}

/**
 * Removes blank form values before submission.
 *
 * @param {Record<string, string>} metadata Metadata fields.
 * @returns {Record<string, string>}
 */
function compactMetadata(metadata) {
  return Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => String(value || '').trim())
  );
}

/**
 * Returns the metadata payload expected by the backend catalog.
 *
 * @returns {Record<string, string>}
 */
function metadataPayload() {
  const base = {
    summary: form.value.summary,
    note: form.value.note,
  };

  if (eventType.value === 'store_visit') {
    return compactMetadata({
      ...base,
      store_name: form.value.store_name,
      visit_reason: form.value.visit_reason,
      sales_associate_name: form.value.sales_associate_name,
    });
  }

  if (needsProductPlaceholder.value) {
    if (selectedProduct.value) {
      return compactMetadata({
        commerce_shopify_product_id: selectedProduct.value.id,
        commerce_shopify_product_variant_id: selectedVariant.value?.id,
        note: form.value.note,
      });
    }

    return compactMetadata({
      product_title: form.value.product_title,
      note: form.value.note,
    });
  }

  if (eventType.value === 'offer_shared') {
    return compactMetadata({
      offer_title: form.value.offer_title,
      offer_code: form.value.offer_code,
      expires_at: form.value.expires_at,
      value: form.value.value,
      currency: form.value.currency,
      note: form.value.note,
    });
  }

  if (eventType.value === 'follow_up_promised') {
    return compactMetadata({
      ...base,
      follow_up_at: form.value.follow_up_at,
      channel: form.value.channel,
    });
  }

  return compactMetadata(base);
}

/**
 * Submits the manual CRM event.
 *
 * @returns {Promise<void>}
 */
async function submit() {
  if (!canSubmit.value || isSubmitting.value) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    const response = await integrationAPI.createCrmEvent({
      contact_id: selectedContact.value.id,
      event_type: eventType.value,
      metadata: metadataPayload(),
    });
    emit('created', response.data);
    useAlert(t('CRM_EVENT_CREATE.SUCCESS'));
    close();
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || t('CRM_EVENT_CREATE.ERROR');
  } finally {
    isSubmitting.value = false;
  }
}

watch(eventType, () => {
  resetForm();
});
watch(
  () => props.preselectedContact,
  contact => {
    selectedContact.value = contact;
  }
);
defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="t('CRM_EVENT_CREATE.TITLE')"
    :confirm-button-label="t('CRM_EVENT_CREATE.SUBMIT')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    @confirm="submit"
  >
    <div class="flex flex-col gap-4">
      <p v-if="errorMessage" class="text-sm text-n-ruby-10">
        {{ errorMessage }}
      </p>

      <label class="flex flex-col gap-1.5 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('CRM_EVENT_CREATE.EVENT_TYPE') }}
        </span>
        <select
          v-model="eventType"
          class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
        >
          <option
            v-for="option in eventTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <CrmContactSelector
        v-if="showContactPicker"
        :key="contactSelectorResetKey"
        :selected-contact="selectedContact"
        @update:selected-contact="selectedContact = $event"
      />

      <div
        v-if="selectedContact"
        class="rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ contactDisplayName(selectedContact) }}
        </p>
        <p v-if="selectedContactSubtitle" class="text-xs text-n-slate-10">
          {{ selectedContactSubtitle }}
        </p>
      </div>

      <div v-if="needsProductPlaceholder" class="grid gap-3">
        <CrmProductSelector
          :key="`${eventType}-${productSelectorResetKey}`"
          :selected-product="selectedProduct"
          @update:selected-product="selectedProduct = $event"
          @update:selected-variant-id="selectedVariantId = $event"
        />
        <label
          v-if="selectedProductVariants.length > 1"
          class="flex flex-col gap-1.5 text-sm"
        >
          <span class="font-medium text-n-slate-12">
            {{ t('CRM_EVENT_CREATE.VARIANT') }}
          </span>
          <select
            v-model="selectedVariantId"
            class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
          >
            <option
              v-for="variant in selectedProductVariants"
              :key="variant.id"
              :value="variant.id"
            >
              {{ [variant.label, variant.sku].filter(Boolean).join(' · ') }}
            </option>
          </select>
        </label>
        <div
          v-if="selectedProduct"
          class="rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CRM_EVENT_CREATE.SELECTED_PRODUCT') }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ selectedProduct.title }}
          </p>
        </div>
        <label class="flex flex-col gap-1.5 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('CRM_EVENT_CREATE.NOTE') }}
          </span>
          <textarea
            v-model="form.note"
            rows="3"
            class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
      </div>

      <div v-else-if="eventType === 'offer_shared'" class="grid gap-3">
        <label class="flex flex-col gap-1.5 text-sm">
          <span class="font-medium text-n-slate-12">
            {{ t('CRM_EVENT_CREATE.OFFER_TITLE') }}
          </span>
          <input
            v-model="form.offer_title"
            class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
          />
        </label>
        <div class="grid grid-cols-2 gap-3">
          <input
            v-model="form.offer_code"
            class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('CRM_EVENT_CREATE.OFFER_CODE')"
          />
          <input
            v-model="form.expires_at"
            type="date"
            class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
          />
        </div>
        <textarea
          v-model="form.note"
          rows="3"
          class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          :placeholder="t('CRM_EVENT_CREATE.NOTE')"
        />
      </div>

      <div v-else-if="eventType === 'follow_up_promised'" class="grid gap-3">
        <textarea
          v-model="form.summary"
          rows="3"
          class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          :placeholder="t('CRM_EVENT_CREATE.SUMMARY')"
        />
        <input
          v-model="form.follow_up_at"
          type="datetime-local"
          class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
        />
      </div>

      <div v-else class="grid gap-3">
        <textarea
          v-model="form.summary"
          rows="3"
          class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          :placeholder="t('CRM_EVENT_CREATE.SUMMARY')"
        />
        <div v-if="eventType === 'store_visit'" class="grid grid-cols-2 gap-3">
          <input
            v-model="form.store_name"
            class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('CRM_EVENT_CREATE.STORE_NAME')"
          />
          <input
            v-model="form.visit_reason"
            class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :placeholder="t('CRM_EVENT_CREATE.VISIT_REASON')"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
